import { Grid, MenuItem, Select, Typography } from '@material-ui/core';
import { cloneDeep } from 'lodash-es';
import { MTableToolbar } from 'material-table';
import React, { ReactElement } from 'react';
import update from 'react-addons-update';

import { track } from '~/src/dash/analytics/analytics';
import {
  cloneMonitor,
  deleteMonitor,
  getAllMonitors,
  updateMonitor,
} from '~/src/dash/api/monitors/monitors_methods';
import { EMonitorTriggerType, IMonitor } from '~/src/dash/api/monitors/monitors_models';
import { EBool } from '~/src/dash/api/types/bool';
import { IListTableColumn, IMonitorRowData, ListTable } from '~/src/dash/components/list-table';
import { MenuButton, renderMenuTitle } from '~/src/dash/components/menu-button';
import { ModelMediumButton } from '~/src/dash/components/model-medium-button';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import {
  getMonitorTriggerType,
  renderMonitorStatus,
} from '~/src/dash/components/monitor/monitor_utils';
import { Panel, Panels } from '~/src/dash/publicaUI';
import { SESSION_ACTIONS_SET } from '~/src/dash/reducers/session-reducers';
import { IStore } from '~/src/dash/reducers/store';
import { getColorTheme } from '~/src/dash/resellers/utils/importResellerConfig';
import {
  extractRouteParams,
  getRoutePath,
  navigate,
  navigateToBulkUpdateRoute,
  RouteMonitorBulkUpdate,
  RouteMonitorBulkUpload,
  RouteMonitorEdit,
  RouteMonitorNew,
} from '~/src/dash/router/routes';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
import { decorate } from '~/src/dash/utils/decorator';
import { obfuscateMonitorName } from '~/src/dash/utils/obfuscate';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { routeName } from '~/src/dash/utils/routes';
import { RowButton } from '~/src/dash/views/component-view';
import { LoadingView } from '~/src/dash/views/loading-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

const MONITOR_ACTIVE_FILTER_ACTIVE: string = '2';
const MONITOR_ACTIVE_FILTER_INACTIVE: string = '1';
const MONITOR_ACTIVE_FILTER_SHOW_ALL: string = '0';

const MONITOR_STATUS_FILTER_ALERT: string = '3';
const MONITOR_STATUS_FILTER_WARNING: string = '2';
const MONITOR_STATUS_FILTER_RECOVERY: string = '1';
const MONITOR_STATUS_FILTER_SHOW_ALL: string = '0';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState extends IBaseState {
  allMonitors?: IMonitor[];
  monitors?: IMonitor[];
  monitorListData: IMonitorRowData[];

  loading: boolean;
  updating: boolean;

  // filters dropdown
  activeMonitorFilter: string;
  statusMonitorFilter: string;
  forceMenuButtonClosed: boolean;

  // Snackbar messages
  snackbarMessage: string;
  snackbarError: boolean;
}

export const MonitorListView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: () => ({
      toolbarRow: {
        marginLeft: -10,
        width: '100%',
        marginTop: 5,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      allMonitors: [] as IMonitor[],
      monitors: [] as IMonitor[],
      monitorListData: [] as IMonitorRowData[],
      loading: false,
      updating: false,
      forceMenuButtonClosed: false,
      activeMonitorFilter:
        this.props.session.searches.MonitorActive || MONITOR_ACTIVE_FILTER_ACTIVE,
      statusMonitorFilter:
        this.props.session.searches.MonitorStatus || MONITOR_STATUS_FILTER_SHOW_ALL,
      snackbarMessage: '',
      snackbarError: false,
    };

    componentDidMount() {
      this.getAllMonitors();
    }

    getAllMonitors = () => {
      this.setState(
        {
          loading: true,
        },
        () => {
          getAllMonitors(this.props.session.publisher.ID).then(([monitors, err]) => {
            if (err) {
              this.setState({
                snackbarError: true,
                snackbarMessage: 'Error while getting the list of monitors from the API.',
                loading: false,
              });
            } else {
              this.setState(
                {
                  loading: false,
                  allMonitors: monitors,
                },
                () => {
                  this.filterMonitors();
                },
              );
            }
          });
        },
      );
    };

    getMonitorEditRoute = (monitorID: number): string => {
      return getRoutePath(RouteMonitorEdit, extractRouteParams(this.props), {
        monitorid: monitorID,
      });
    };

    // navigates to monitor bulk upload
    goToMonitorBulkUpload = () => {
      navigate(
        RouteMonitorBulkUpload,
        extractRouteParams({
          session: this.props.session,
          user: this.props.user,
        }),
      );
    };

    generateMonitorList = () => {
      const monitorListData = (this.state.monitors || []).map(
        (monitor: IMonitor): IMonitorRowData => {
          return {
            ID: monitor.ID,
            Name: obfuscateMonitorName(monitor, this.props.user.user.Username, monitor.Name),
            Status: getMonitorTriggerType(monitor),
            Active: monitor.Active === EBool.EBool_TRUE,
            SuperUserOnly: monitor.IsSuperAdmin === EBool.EBool_TRUE,
            tableData: {
              checked: false,
            },
          };
        },
      );

      this.setState({
        monitorListData,
      });
    };

    // ----- COMPONENT HELPER  FUNCTIONS ----- //

    handleCloseSnackbar = (): void => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    handleSetUpdating = (updating: boolean) => {
      this.setState({
        updating,
      });
    };

    updateMonitor = (monitor: IMonitor, override: Partial<IMonitor>) => {
      this.setState(
        {
          updating: true,
        },
        () => {
          updateMonitor(Object.assign(monitor, override), monitor.PublisherID.toString()).then(
            (res: [IMonitor | undefined, string | undefined]) => {
              const err = Array.isArray(res) ? res[1] : null;
              if (err) {
                console.error(err);
                this.setState({
                  snackbarMessage: 'Error while updating monitor',
                  snackbarError: true,
                  updating: false,
                });
              } else {
                this.setState(
                  {
                    snackbarMessage: 'Successfully saved monitor!',
                    updating: false,
                  },
                  this.getAllMonitors,
                );
              }
            },
          );
        },
      );
    };

    handleChangeActiveMonitor = (id: number, checked: boolean) => {
      const active = checked ? EBool.EBool_TRUE : EBool.EBool_FALSE;
      for (const monitor of this.state.monitors) {
        if (monitor.ID === id) {
          this.updateMonitor(monitor, {
            Active: active,
          } as Partial<IMonitor>);
        }
      }
    };

    handleSuperOnlyChange = (id: number, checked: boolean) => {
      const isSuperUser = checked ? EBool.EBool_TRUE : EBool.EBool_FALSE;
      for (const monitor of this.state.monitors) {
        if (monitor.ID === id) {
          this.updateMonitor(monitor, {
            IsSuperAdmin: isSuperUser,
          } as Partial<IMonitor>);
        }
      }
    };

    // handles selection of a row in the list table
    handleSelectionChange = (selectedRows: IMonitorRowData[]) => {
      const selectedIDsMap = {} as { [key: string]: boolean };
      const data = cloneDeep(this.state.monitorListData);
      for (const selectedRow of selectedRows) {
        selectedIDsMap[selectedRow.ID] = true;
      }

      for (let i = 0; i < data.length; i++) {
        data[i].tableData.checked = !!(data[i].ID in selectedIDsMap);
      }

      this.setState({
        monitorListData: data as IMonitorRowData[],
      });
    };

    filterMonitors = () => {
      const { user } = this.props;
      const { activeMonitorFilter, statusMonitorFilter } = this.state;

      this.setState({}, () => {
        let monitors = cloneDeep(this.state.allMonitors);

        // if not super user, remove monitors that are super only
        if (!hasRoleSuperUser(user.user)) {
          monitors = monitors.filter((row: IMonitor) => row.IsSuperAdmin !== EBool.EBool_TRUE);
        }

        // filter by monitors by active filter
        if (activeMonitorFilter === MONITOR_ACTIVE_FILTER_ACTIVE) {
          monitors = monitors.filter((row: IMonitor) => row.Active === EBool.EBool_TRUE);
        } else if (activeMonitorFilter === MONITOR_ACTIVE_FILTER_INACTIVE) {
          monitors = monitors.filter((row: IMonitor) => row.Active === EBool.EBool_FALSE);
        }

        // filter by monitors by status filter
        if (statusMonitorFilter === MONITOR_STATUS_FILTER_ALERT) {
          monitors = monitors.filter(
            (row: IMonitor) =>
              getMonitorTriggerType(row) === EMonitorTriggerType.MonitorTriggerType_ALERT,
          );
        } else if (statusMonitorFilter === MONITOR_STATUS_FILTER_WARNING) {
          monitors = monitors.filter(
            (row: IMonitor) =>
              getMonitorTriggerType(row) === EMonitorTriggerType.MonitorTriggerType_WARNING,
          );
        } else if (statusMonitorFilter === MONITOR_STATUS_FILTER_RECOVERY) {
          monitors = monitors.filter(
            (row: IMonitor) =>
              getMonitorTriggerType(row) ===
                EMonitorTriggerType.MonitorTriggerType_ALERT_RECOVERY ||
              getMonitorTriggerType(row) ===
                EMonitorTriggerType.MonitorTriggerType_WARNING_RECOVERY,
          );
        }

        this.setState(
          {
            monitors,
          },
          this.generateMonitorList,
        );
      });
    };

    // ----- HANDLER HELPER FUNCTIONS FOR LIST TABLE ----- //

    // deletes the monitor and gives user msg if success/failure, returns a promise
    // for ModelDialogDeleteView which requires a promise to close dialog
    handleDeleteMonitor = (monitorID: string) => {
      deleteMonitor(monitorID, this.props.session.publisher.ID)
        .then(() => {
          this.setState(
            {
              snackbarMessage: 'Successfully deleted monitor',
              snackbarError: false,
            },
            this.getAllMonitors,
          );
        })
        .catch((err) => {
          console.error(err);
          this.setState({
            snackbarMessage: 'Error while deleting monitor',
            snackbarError: true,
          });
        });
      // the delete modal expects a promise to be returned
      return new Promise((resolve) => resolve(undefined));
    };

    handleCloneMonitor = (monitorID: string): void => {
      cloneMonitor(monitorID, this.props.session.publisher.ID)
        .then(([clonedMonitorID]) => {
          if (clonedMonitorID) {
            navigate(
              this.getMonitorEditRoute(clonedMonitorID),
              extractRouteParams({
                session: this.props.session,
                user: this.props.user,
              }),
            );
          }
        })
        .catch((err) => {
          console.error(err);
          this.setState({
            snackbarMessage: 'Error while cloning monitor',
            snackbarError: true,
          });
        });
    };

    // Cache search/filter on change
    handleSearchChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      this.setState(
        {
          [target.name]: target.value,
          page: 0,
        },
        () => {
          switch (target.name) {
            case 'activeMonitorFilter':
              this.props.dispatch(
                SESSION_ACTIONS_SET({
                  searches: update(this.props.session.searches, {
                    MonitorActive: {
                      $set: target.value,
                    },
                  }),
                }),
              );
              break;
            case 'statusMonitorFilter':
              this.props.dispatch(
                SESSION_ACTIONS_SET({
                  searches: update(this.props.session.searches, {
                    MonitorStatus: {
                      $set: target.value,
                    },
                  }),
                }),
              );
              break;
          }
          this.filterMonitors();
        },
      );
    };

    renderActiveDropdown = () => {
      const { activeMonitorFilter } = this.state;
      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container direction='row' alignItems='center' spacing={2}>
            <Grid item xs={3}>
              <Typography style={{ fontWeight: 500, fontSize: 12 }}>Active</Typography>
            </Grid>
            <Grid item xs={9}>
              <Select
                name='activeMonitorFilter'
                value={activeMonitorFilter}
                onChange={this.handleSearchChange}
                style={{ fontSize: '14px' }}
              >
                <MenuItem value={MONITOR_ACTIVE_FILTER_ACTIVE}>Active</MenuItem>
                <MenuItem value={MONITOR_ACTIVE_FILTER_INACTIVE}>Inactive</MenuItem>
                <MenuItem value={MONITOR_ACTIVE_FILTER_SHOW_ALL}>Show All</MenuItem>
              </Select>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderStatusDropdown = () => {
      const { statusMonitorFilter } = this.state;
      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container direction='row' alignItems='center' spacing={2}>
            <Grid item xs={3}>
              <Typography style={{ fontWeight: 500, fontSize: 12 }}>Status</Typography>
            </Grid>
            <Grid item xs={9}>
              <Select
                name='statusMonitorFilter'
                value={statusMonitorFilter}
                onChange={this.handleSearchChange}
                style={{ fontSize: '14px' }}
              >
                <MenuItem value={MONITOR_STATUS_FILTER_ALERT}>Alert</MenuItem>
                <MenuItem value={MONITOR_STATUS_FILTER_WARNING}>Warning</MenuItem>
                <MenuItem value={MONITOR_STATUS_FILTER_RECOVERY}>Recovery</MenuItem>
                <MenuItem value={MONITOR_STATUS_FILTER_SHOW_ALL}>Show All</MenuItem>
              </Select>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderFilterAndBulkUpdateButton = (): ReactElement => {
      const selectedMonitorIDs = Object.values(this.state.monitorListData)
        .filter((datum) => datum.tableData.checked)
        .map((datum) => datum.ID);
      const statusDropdown = {
        closeOnClick: false,
        render: this.renderStatusDropdown,
      };
      const activeDropdown = {
        closeOnClick: false,
        render: this.renderActiveDropdown,
      };
      const menuButtonItems = [activeDropdown, statusDropdown];

      return (
        <Grid container direction='row' spacing={3} alignItems='center'>
          <Grid
            item
            onClick={() => {
              this.setState({
                forceMenuButtonClosed: false,
              });
            }}
          >
            <MenuButton
              forceClosed={this.state.forceMenuButtonClosed}
              titleRender={renderMenuTitle}
              items={menuButtonItems}
              minWidth={450}
              marginTop={60}
              marginLeft={0}
            />
          </Grid>
          {selectedMonitorIDs.length > 1 && (
            <Grid
              item
              onClick={() => {
                // @ts-ignore
                navigateToBulkUpdateRoute(
                  RouteMonitorBulkUpdate,
                  selectedMonitorIDs,
                  extractRouteParams(this.props),
                );
              }}
            >
              <Typography
                style={{
                  color: selectedMonitorIDs.length > 0 ? '#5D66FA' : '#707070',
                  fontWeight: 500,
                  marginLeft: 10,
                  cursor: 'pointer',
                }}
              >
                {`Update ${selectedMonitorIDs.length} Selected Monitors`}
              </Typography>
            </Grid>
          )}
        </Grid>
      );
    };

    // renderMonitorToolbar renders the toolbar at the top of the list table
    renderMonitorToolbar = (props: any): ReactElement => {
      const { classes } = this.props;
      const isSuperUser = hasRoleSuperUser(this.props.user.user);
      const copyProps = {
        ...props,
        searchFieldStyle: { ...props.searchFieldStyle, marginLeft: '-48px', width: '300px' },
      };

      return (
        <Grid item className={classes.toolbarRow}>
          <Grid container direction='row' justify='space-between' alignItems='center'>
            <Grid item>
              <Grid
                container
                direction='column'
                justify='flex-start'
                alignItems='flex-start'
                spacing={0}
              >
                <Grid item>
                  {/* grid with MTableToolbarWrapper class is required for auto focusing search upon loading the list table component */}
                  <Grid item className='MTableToolbarWrapper'>
                    <MTableToolbar {...copyProps} />
                  </Grid>
                </Grid>
                <Grid item style={{ marginLeft: 12 }}>
                  {this.renderFilterAndBulkUpdateButton()}
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container alignItems='center' spacing={2}>
                {/* RIGHT - + Bulk Monitor Upload - only super for now */}
                {
                  // TODO: build the bulk upload feature in the next pr
                  false && isSuperUser && (
                    <Grid item>
                      <RowButton
                        right={
                          <ModelMediumButton
                            variant='contained'
                            color='primary'
                            style={{ paddingLeft: '30px', paddingRight: '30px' }}
                            onClick={this.goToMonitorBulkUpload}
                          >
                            Bulk Upload
                          </ModelMediumButton>
                        }
                      />
                    </Grid>
                  )
                }
                <Grid item>
                  <ModelMediumButton
                    variant='contained'
                    onClick={() => {
                      track('Monitor List - Create New', {}, this.props.user.user);
                      navigate(RouteMonitorNew, extractRouteParams(this.props));
                    }}
                    style={{
                      backgroundColor: getColorTheme('channelBidderPrimaryButton'),
                      boxShadow: `0px 0px 6px ${getColorTheme('channelBidderPrimaryButtonShadow')}`,
                      color: getColorTheme('channelBidderPrimaryButtonText'),
                    }}
                  >
                    + New Monitor
                  </ModelMediumButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    render() {
      const { loading, monitors, monitorListData, snackbarError, snackbarMessage, updating } =
        this.state;
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      if (!!monitors && monitors.length === 0 && loading) {
        return <LoadingView active={true} text='Loading the monitor list' />;
      }

      const isSuperUser = hasRoleSuperUser(this.props.user.user);
      const listTableColumns: IListTableColumn[] = [
        {
          field: 'Active',
          title: 'Active',
          renderType: 'activeswitch',
          searchable: false,
          sorting: false,
          width: '15%',
        },
        { field: 'Name', title: 'Name', renderType: 'namelink', width: '30%' },
        {
          field: 'Status',
          title: 'Status',
          render: (rowData: IMonitorRowData) => renderMonitorStatus(rowData.Status),
          searchable: false,
          width: '20%',
        },
        {
          field: 'Actions',
          title: 'Actions',
          renderType: 'deleteclone',
          searchable: false,
          width: '15%',
        },
      ];
      if (isSuperUser) {
        listTableColumns.splice(2, 0, {
          field: 'SuperUserOnly',
          title: 'Super-user Only *',
          renderType: 'superuserswitch',
          searchable: false,
          width: '15%',
        });
      }

      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largePadding>
            <ListTable
              title=''
              columns={listTableColumns}
              data={monitorListData}
              isLoading={loading}
              isUpdating={updating}
              getItemSettingRoute={this.getMonitorEditRoute}
              handleSetUpdating={this.handleSetUpdating}
              handleDeleteItem={this.handleDeleteMonitor}
              handleCloneItem={this.handleCloneMonitor}
              handleChangeActive={this.handleChangeActiveMonitor}
              handleSuperOnlyChange={this.handleSuperOnlyChange}
              renderCustomToolbar={this.renderMonitorToolbar}
              fixedEnabled={true}
              pageSize={10}
              selectionEnabled={true}
              handleSelectionChange={this.handleSelectionChange}
              secondTabSelection={true}
            />
            <SnackbarView
              open={!!snackbarMessage}
              error={snackbarError}
              onClose={this.handleCloseSnackbar}
            >
              {snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);
